// src/pages/admin/inhouse/ProductForm.jsx
import React, { useContext, useEffect, useState, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { SidebarContext } from "../../../context/Sidebar";
import { Row, Col, Card, Form, Spinner, Alert, Button } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import { FiUpload, FiX, FiSave, FiArrowLeft } from "react-icons/fi";

export default function InHouseProductForm() {
    const { t } = useContext(TranslatorContext);
    const { setSidebar } = useContext(SidebarContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const AuthToken = localStorage.getItem("authToken");
    const isEditMode = !!id;

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [removeImage, setRemoveImage] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        purchase_unit: "",
        unit_type: "",
        unit_quantity: 1,
        minimum_alert_qty: 0,
        description: "",
        is_active: 1
    });


    // Fetch product data if edit mode
    const fetchProduct = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/inhouse/products/${id}`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            const product = response.data.data;
            setFormData({
                name: product.name || "",
                purchase_unit: product.purchase_unit || "",
                unit_type: product.unit_type || "",
                unit_quantity: product.unit_quantity || 1,
                minimum_alert_qty: product.minimum_alert_qty || 0,
                description: product.description || "",
                is_active: product.is_active ? 1 : 0
            });
            if (product.img) {
                setImagePreview(`${process.env.REACT_APP_IMG_URL}/${product.img}`);
            }
        } catch (err) {
            console.error("Error fetching product:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to load product data'),
                showConfirmButton: false,
                timer: 2000
            });
        }
    }, [id, AuthToken, t]);

    useEffect(() => {
        setSidebar(false);
        const loadData = async () => {
            setLoading(true);
            if (isEditMode) {
                await fetchProduct();
            }
            setLoading(false);
        };
        loadData();
    }, [fetchProduct, isEditMode, setSidebar]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
        setSubmitError(null);
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numValue = parseFloat(value);
        setFormData(prev => ({ ...prev, [name]: isNaN(numValue) ? 0 : numValue }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Image size must be less than 2MB'),
                    showConfirmButton: false,
                    timer: 2000
                });
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setRemoveImage(false);
        }
    };

    const handleRemoveImage = () => {
        setImagePreview(null);
        setImageFile(null);
        setRemoveImage(true);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setErrors({});
        setSubmitError(null);

        // Validation
        const validationErrors = {};
        if (!formData.name) validationErrors.name = t("Product name is required");
        if (!formData.unit_type) validationErrors.unit_type = t("Unit type is required");
        if (!formData.unit_quantity || formData.unit_quantity <= 0) validationErrors.unit_quantity = t("Unit quantity must be greater than 0");
        
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setSubmitting(false);
            return;
        }

        const submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('purchase_unit', formData.purchase_unit || '');
        submitData.append('unit_type', formData.unit_type);
        submitData.append('unit_quantity', formData.unit_quantity);
        submitData.append('minimum_alert_qty', formData.minimum_alert_qty || 0);
        submitData.append('description', formData.description || '');
        submitData.append('is_active', formData.is_active);

        if (imageFile) {
            submitData.append('img', imageFile);
        }
        
        if (removeImage && isEditMode) {
            submitData.append('remove_img', '1');
        }

        try {
            let response;
            if (isEditMode) {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/inhouse/products/${id}`,
                    submitData,
                    {
                        headers: {
                            Authorization: `Bearer ${AuthToken}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            } else {
                response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/inhouse/products`,
                    submitData,
                    {
                        headers: {
                            Authorization: `Bearer ${AuthToken}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            }

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: t(isEditMode ? "Product Updated!" : "Product Created!"),
                    text: t(isEditMode ? "Product has been updated successfully." : "Product has been created successfully."),
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('OK')
                });
                navigate("/inhouse/products");
            } else {
                setSubmitError(response.data.message || `Failed to ${isEditMode ? 'update' : 'create'} product`);
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors);
            } else if (err.response?.data?.message) {
                setSubmitError(err.response.data.message);
            } else {
                console.error(err);
                setSubmitError(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the product`);
            }
        } finally {
            setSubmitting(false);
        }
    };


    if (loading) {
        return (
            <PageLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">{t("Loading...")}</p>
                    </div>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="container-fluid px-0">
                {/* Page Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">{isEditMode ? t("Edit Product") : t("Create New Product")}</h4>
                        <p className="text-muted mb-0">
                            {isEditMode ? t("Update product information") : t("Add a new product to inventory")}
                        </p>
                    </div>
                    <Button
                        variant="secondary"
                        onClick={() => navigate("/inhouse/products")}
                        className="d-flex align-items-center gap-2"
                    >
                        <FiArrowLeft /> {t("Back to Products")}
                    </Button>
                </div>

                {/* Error Alert */}
                {submitError && (
                    <Alert variant="danger" className="mb-4" onClose={() => setSubmitError(null)} dismissible>
                        <Alert.Heading>{t("Error")}</Alert.Heading>
                        <p>{submitError}</p>
                    </Alert>
                )}

                <Row>
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Basic Information")}</h5>
                                
                                <Row className="g-4">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {t("Product Name")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder={t("Enter product name")}
                                                isInvalid={!!errors.name}
                                                className="py-2"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t("Description")}</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder={t("Enter product description...")}
                                                isInvalid={!!errors.description}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.description}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h5 className="mt-4 mb-4 pb-2 border-bottom">{t("Unit Information")}</h5>
                                
                                <Row className="g-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {t("Purchase Unit")}
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="purchase_unit"
                                                value={formData.purchase_unit}
                                                onChange={handleChange}
                                                placeholder={t("e.g., Box, Packet, Bottle")}
                                                isInvalid={!!errors.purchase_unit}
                                            />
                                            <Form.Text className="text-muted">
                                                {t("The unit you buy from vendor")}
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.purchase_unit}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {t("Unit Type")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="unit_type"
                                                value={formData.unit_type}
                                                onChange={handleChange}
                                                placeholder={t("e.g., Pcs, ml, L, g, kg, mg")}
                                                list="unitTypes"
                                                isInvalid={!!errors.unit_type}
                                            />
                                            <datalist id="unitTypes">
                                                <option value="Pcs">{t("Pieces")}</option>
                                                <option value="ml">{t("Milliliter")}</option>
                                                <option value="L">{t("Liter")}</option>
                                                <option value="g">{t("Gram")}</option>
                                                <option value="kg">{t("Kilogram")}</option>
                                                <option value="mg">{t("Milligram")}</option>
                                            </datalist>
                                            <Form.Text className="text-muted">
                                                {t("Base unit for stock management")}
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.unit_type}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {t("Unit Quantity")} <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="unit_quantity"
                                                value={formData.unit_quantity}
                                                onChange={handleNumberChange}
                                                step="0.01"
                                                min="0.01"
                                                placeholder={t("e.g., 5")}
                                                isInvalid={!!errors.unit_quantity}
                                            />
                                            <Form.Text className="text-muted">
                                                {t("How many units per purchase unit")}
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.unit_quantity}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h5 className="mt-4 mb-4 pb-2 border-bottom">{t("Inventory Settings")}</h5>
                                
                                <Row className="g-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {t("Minimum Alert Quantity")}
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="minimum_alert_qty"
                                                value={formData.minimum_alert_qty}
                                                onChange={handleNumberChange}
                                                step="1"
                                                min="0"
                                                placeholder={t("0")}
                                                isInvalid={!!errors.minimum_alert_qty}
                                            />
                                            <Form.Text className="text-muted">
                                                {t("Get alert when stock falls below this level")}
                                            </Form.Text>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.minimum_alert_qty}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">{t("Status")}</Form.Label>
                                            <div className="d-flex gap-4 mt-2">
                                                <Form.Check
                                                    type="radio"
                                                    label={t("Active")}
                                                    name="is_active"
                                                    value="1"
                                                    checked={formData.is_active === 1}
                                                    onChange={handleChange}
                                                    id="status-active"
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    label={t("Inactive")}
                                                    name="is_active"
                                                    value="0"
                                                    checked={formData.is_active === 0}
                                                    onChange={handleChange}
                                                    id="status-inactive"
                                                />
                                            </div>
                                            <Form.Text className="text-muted">
                                                {t("Inactive products won't appear in purchases")}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 pb-2 border-bottom">{t("Product Image")}</h5>
                                
                                <div className="text-center">
                                    {imagePreview ? (
                                        <div className="position-relative d-inline-block">
                                            <img
                                                src={imagePreview}
                                                alt={t("Product preview")}
                                                style={{
                                                    width: '100%',
                                                    maxHeight: '250px',
                                                    objectFit: 'contain',
                                                    borderRadius: '8px',
                                                    border: '1px solid #dee2e6',
                                                    padding: '8px',
                                                    backgroundColor: '#f8f9fa'
                                                }}
                                            />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className="position-absolute top-0 end-0 rounded-circle p-1"
                                                style={{ transform: 'translate(25%, -25%)' }}
                                                onClick={handleRemoveImage}
                                            >
                                                <FiX size={14} />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div 
                                            className="border rounded-3 p-5 text-center bg-light"
                                            style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                            onClick={() => document.getElementById('image-input').click()}
                                        >
                                            <FiUpload size={48} className="text-muted mb-3" />
                                            <p className="mb-1 text-muted">{t("Click to upload image")}</p>
                                            <small className="text-muted">
                                                {t("JPEG, PNG, JPG, WEBP up to 2MB")}
                                            </small>
                                        </div>
                                    )}
                                    
                                    <Form.Control
                                        id="image-input"
                                        type="file"
                                        name="img"
                                        onChange={handleImageChange}
                                        accept="image/jpeg,image/png,image/jpg,image/webp"
                                        className="d-none"
                                    />
                                    
                                    {errors.img && (
                                        <div className="text-danger small mt-2">{errors.img}</div>
                                    )}
                                </div>

                                <hr className="my-4" />

                                <div className="bg-light p-3 rounded">
                                    <h6 className="mb-3">{t("Information")}</h6>
                                    <div className="small text-muted">
                                        <p className="mb-2">
                                            <strong>{t("Note")}:</strong> {t("Sale price is set during purchase and can vary per batch.")}
                                        </p>
                                        <p className="mb-0">
                                            <strong>{t("Stock Calculation")}:</strong><br />
                                            {t("Total stock = (Purchase Qty × Unit Quantity) + Extra Loose Qty")}
                                        </p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Action Buttons */}
                <div className="mt-4 d-flex gap-3 justify-content-end">
                    <Button
                        variant="light"
                        onClick={() => navigate("/inhouse/products")}
                        className="px-4"
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-4 d-flex align-items-center gap-2"
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" />
                                <span>{t("Saving...")}</span>
                            </>
                        ) : (
                            <>
                                <FiSave />
                                <span>{isEditMode ? t("Update Product") : t("Create Product")}</span>
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </PageLayout>
    );
}